import React from 'react';
import ReactDOM from 'react-dom';
import { Point } from '../coreui';
import { Settings } from '../../CommonTypes';
import { fromBack, sufficientlyDifferent } from '../../react_utils';
import { SideScroll } from '../coreui/basic';
import { getMousePoint, IMeasureToolProps, ITool } from './Base';
import { IconsStyled } from './Creator.styles';

import { lines, LineStrip, SingleLine, Polygon, SingleLineIcon, LineStripIcon, PolygonIcon } from './Line';
import ReactTooltip from 'react-tooltip';
import { localization } from '../../localization';

export const tools = [...lines] as const
export type ToolType = typeof tools[number];

const toolIcons = [SingleLineIcon, LineStripIcon, PolygonIcon];

export const getTool = (tool: number) => {
  switch (tools[tool]) {
    case 'SingleLine': return SingleLine;
    case 'LineStrip': return LineStrip;
    case 'Polygon': return Polygon;
  }
};

export type SerializedTool = { name?: string; data: Point[]; tool: number; hidden: number; };
export interface IToolWrapped extends ITool {
  serialize: () => SerializedTool;
}

interface IWrappedState {
  name?: string;
  data: Point[];
  hidden: number;
}

let toolKey = 0;

export const ToolWrapper = (initialState: IWrappedState, tool: number) => {
  const Tool = getTool(tool);
  
  class ToolWrapped extends React.Component<IMeasureToolProps, IWrappedState> implements IToolWrapped {
    private orig: ITool | null = null;
    constructor(props: IMeasureToolProps) {
      super(props);
      this.state = { ...initialState }
    }

    private updateName = (newName: string) => this.setState({ name: newName });

    private updateData = (newData: Point[] | null) => {
      if (newData && newData.length >= 2) {
        this.setState({ data: newData });
      } else if (this.props.onDelete) {
        this.props.onDelete();
      }
    };

    componentDidMount = () => {
      this.setState({ ...initialState });
    };
  
    componentWillUnmount = () => {
      initialState = { ...this.state };
    };

    public getCSVEntries = () => {
      if (this.orig)
        return this.orig.getCSVEntries();
      return null;
    };

    public serialize = () => ({
      name: this.state.name,
      data: this.state.data,
      tool: tool,
      hidden: this.state.hidden
    });

    public render() {
      return React.createElement(Tool, {
        ...this.props,
        name: this.state.name,
        data: this.state.data,
        hidden: this.state.hidden,
        onHiddenChange: newHidden => this.setState({ hidden: newHidden}),
        onUpdateName: this.updateName,
        onUpdateData: this.updateData,
        ref: elem => this.orig = elem
      });
    }
  }
  
  return { tool: ToolWrapped, key: toolKey++ };
};

export type MeasureToolWrapped = ReturnType<typeof ToolWrapper>;


export interface IMTCProps {
  allowCreation: boolean;
  creationIconsPortal: HTMLDivElement | null;
  renderPortal: {
    portal: SVGSVGElement | null;
    overlayScale: number;
  }
  lengthScale: number;
  unit: string;
  settings: Settings;

  onCreationStart: () => void;
  onCreationDone: (newTool?: MeasureToolWrapped) => void;
}

export interface IMTCState {
  activeTool: number;
  active?: {
    data: Point[];
    mouse: Point;
  }
}

export class MeasureToolCreator extends React.Component<IMTCProps, IMTCState> {
  constructor(props: IMTCProps) {
    super(props);

    this.state = {
      activeTool: 0
    }
  }

  private creationDone = (data: Point[]) => {
    this.setState({active: undefined });
    this.props.onCreationDone(data.length >= 2 ? ToolWrapper({ data, hidden: 0 }, this.state.activeTool) : undefined);
  };

  private appendPoint = (e: MouseEvent) => {
    const target = this.props.renderPortal.portal;
    if (target) {
      const newPoint = getMousePoint(e, target);
      if (this.state.active) {
        const { different } = sufficientlyDifferent(newPoint, fromBack(this.state.active.data, 1), this.props.renderPortal.overlayScale);
        if (!different)
          return;
      }
      const oldData = this.state.active ? this.state.active.data : [newPoint];
    
      this.setState({
        active: { data: [...oldData, newPoint], mouse: { x: e.clientX, y: e.clientY } }
      });
    }
  };

  private doubleClick = (e: MouseEvent) => {
    if (e.button === 0 && this.state.active) {
      this.creationDone(this.state.active.data.slice(0, -1));
    }
  }

  private mouseDown = (e: MouseEvent) => {
    const target = this.props.renderPortal.portal;
    if (this.props.allowCreation && e.button === 0 && target) {
      e.preventDefault();
      if (!this.state.active)
        this.props.onCreationStart();
      this.appendPoint(e);
    }
  };

  private mouseUp = (e: MouseEvent) => {
    if (this.props.allowCreation && this.state.active && e.button === 0) {
      e.preventDefault();
      this.appendPoint(e);
    }
  };

  private mouseMove = (e: MouseEvent) => {
    const target = this.props.renderPortal.portal;
    if (this.state.active && target) {
      const newPoint = getMousePoint(e, target);
      if (newPoint) {
        this.setState({
          active: { data: [...this.state.active.data.slice(0, -1), newPoint], mouse: { x: e.clientX, y: e.clientY } }
        });
      }
    }
  };

  private keyDown = (e: KeyboardEvent) => {
    if (this.state.active) {
      if (e.key === 'Escape') {
        this.setState({ active: undefined });
        this.props.onCreationDone();
      } else if (e.key === 'Enter') {
        this.creationDone(this.state.active.data);
      }
    }
  };

  componentDidMount = () => {
    document.addEventListener('mousemove', this.mouseMove);
    document.addEventListener('mouseup', this.mouseUp);
    document.addEventListener('keydown', this.keyDown);
    const target = this.props.renderPortal.portal;
    if (target) {
      target.addEventListener('mousedown', this.mouseDown);
      target.addEventListener('dblclick', this.doubleClick);
    }
    ReactTooltip.rebuild();
  };

  componentDidUpdate = (prevProps: IMTCProps) => {
    const prevTarget = prevProps.renderPortal.portal;
    const curTarget = this.props.renderPortal.portal;
    if (prevTarget !== curTarget) {
      if (prevTarget) {
        prevTarget.removeEventListener('mousedown', this.mouseDown);
        prevTarget.removeEventListener('dblclick', this.doubleClick);
      } if (curTarget) {
        curTarget.addEventListener('mousedown', this.mouseDown);
        curTarget.addEventListener('dblclick', this.doubleClick);
      }
    }
    if (prevProps.creationIconsPortal !== this.props.creationIconsPortal) {
      ReactTooltip.rebuild();
    }
  };

  componentWillUnmount = () => {
    document.removeEventListener('mousemove', this.mouseMove);
    document.removeEventListener('mouseup', this.mouseUp);
    document.removeEventListener('keydown', this.keyDown);
    
    const target = this.props.renderPortal.portal;
    if (target) {
      target.removeEventListener('mousedown', this.mouseDown);
      target.removeEventListener('dblclick', this.doubleClick);
    }
  };

  public render() {
    let iconProtal: React.ReactPortal | null = null;
    if (this.props.creationIconsPortal) {
      const icons = <SideScroll sideScrollSpeed={20} hideScrollBar>
        <IconsStyled>
          {toolIcons.map((icon, i) => (
            <div key={i} onClick={() => this.setState({ activeTool: i })} style={i === this.state.activeTool ? { opacity: 1 } : {}}
              data-tip={localization[this.props.settings.lang].tooltips.tools[tools[i]] }>
              { React.createElement(icon)}
            </div>
          ))}
        </IconsStyled>
      </SideScroll>;

      iconProtal = ReactDOM.createPortal(icons, this.props.creationIconsPortal);
    }

    return <>
      { iconProtal }
      { this.state.active ? React.createElement(getTool(this.state.activeTool), {
        index: -1,
        data: this.state.active.data,
        unit: this.props.unit,
        settings: this.props.settings,
        lengthScale: this.props.lengthScale,
        allowInteraction: false,
        showTooltip: { x: this.state.active.mouse.x + 15, y: this.state.active.mouse.y + 15 },
        hidden: 0, onHiddenChange: () => { },
        UIPortals: [],
        RenderPortals: [{
          ...this.props.renderPortal,
          requestStyle: () => { }
        }],
        onFinish: this.creationDone
      }) : null }
    </>;
  }
}
